import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Messages } from 'primereact/messages';
import { Button } from 'primereact/button';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';
import { FormContainer, FormInputText, FormDropdown, FormGoogleAddressLookup, FormCheckbox } from '../../components/form';
import { OrganizationalUnit } from '../../models/org-units/orgUnit';
import { LicenseStatus, StateLicense } from '../../models/org-units/stateLicense';
import { Channel } from '../../models/org-units/channel';
import { usStates, USState } from '../../utils/us-states';
import { AddressComponents } from '../../components/form/FormGoogleAddressLookup';

// Props interface for the component
interface EditOrgUnitProps {
  onSave: (_orgUnit: OrganizationalUnit) => void;
  onHide: () => void;
  orgUnit?: OrganizationalUnit;
  parentOrgUnitId?: string;
  apiLoading?: boolean;
}

export default function EditOrgUnit(props: EditOrgUnitProps) {
  const { onSave, onHide, orgUnit, parentOrgUnitId, apiLoading } = props;

  // Reference to the Toast component for showing messages
  const errors = useRef<Messages>(null);

  const { tenant } = useAuth();

  // State for API loading only (no other separate state)
  const [localApiLoading, setLocalApiLoading] = useState(false);

  // Update loading state based on parent props
  useEffect(() => {
    // If parent is explicitly controlling the loading state, use that
    if (apiLoading !== undefined) {
      setLocalApiLoading(apiLoading);
    }
  }, [apiLoading]);

  // Form validation schema
  const validationSchema = yup.object({
    name: yup.string().required('Name is required').trim(),
    channel: yup.string().nullable(),
    nmlsId: yup.string().nullable(),
    address: yup.object({
      street: yup.string().required('Street is required').trim(),
      city: yup.string().required('City is required').trim(),
      county: yup.string().nullable().optional().trim(),
      state: yup.string().required('State is required').trim(),
      postalCode: yup.string().required('Postal code is required').trim(),
    }),
    stateLicenses: yup.array().of(
      yup.object({
        state: yup.string().required('State is required'),
        status: yup.string().required('Status is required'),
        licenseNumber: yup.string().required('License number is required').trim(),
      })
    ),
    isOriginatingBranch: yup.boolean().default(false),
  });

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors },
    setValue,
    watch,
    reset,
  } = useForm<OrganizationalUnit>({
    resolver: yupResolver<any>(validationSchema),
    defaultValues: {
      name: '',
      channel: undefined,
      nmlsId: '',
      address: {
        street: '',
        city: '',
        county: '',
        state: '',
        postalCode: '',
        unitNumber: '',
      },
      stateLicenses: [],
      isOriginatingBranch: false,
    },
  });

  // Initialize form based on provided orgUnit or default values
  useEffect(() => {
    if (orgUnit) {
      // Set the form fields from the orgUnit
      reset({
        ...orgUnit,
        // Ensure these fields are always present
        address: {
          street: orgUnit.address?.street || '',
          city: orgUnit.address?.city || '',
          county: orgUnit.address?.county || '',
          state: orgUnit.address?.state || '',
          postalCode: orgUnit.address?.postalCode || '',
          unitNumber: orgUnit.address?.unitNumber || '',
        },
        stateLicenses: orgUnit.stateLicenses || [],
        isOriginatingBranch: orgUnit.isOriginatingBranch || false,
      });
    } else {
      // Reset form for new org unit
      reset({
        name: '',
        nmlsId: '',
        address: {
          street: '',
          city: '',
          county: '',
          state: '',
          postalCode: '',
        },
        stateLicenses: [],
        isOriginatingBranch: false,
      });
    }
  }, [orgUnit, reset]);

  // Extract values from form
  const watchStateLicenses = watch('stateLicenses') || [];

  // Show errors in the UI if validation fails
  const showFormErrors = () => {
    if (Object.keys(formErrors).length > 0) {
      let errorMessage = 'Please correct the following errors:';

      // Process nested errors
      const processErrors = (errors: any, path = '') => {
        Object.entries(errors).forEach(([key, value]: [string, any]) => {
          const currentPath = path ? `${path}.${key}` : key;

          if (value && typeof value === 'object' && !value.message) {
            // It's a nested error object, recurse
            processErrors(value, currentPath);
          } else if (value.message) {
            // It's an error with a message
            errorMessage += `\n- ${currentPath}: ${value.message}`;
          }
        });
      };

      processErrors(formErrors);

      // Show in the Messages component
      if (errors.current) {
        errors.current.show({
          severity: 'error',
          summary: 'Validation Failed',
          detail: errorMessage,
          life: 10000,
        });
      }

      return true;
    }
    return false;
  };

  // Function to add a state license
  const addStateLicense = () => {
    const newLicense: StateLicense = {
      state: '',
      status: LicenseStatus.Registered,
      licenseNumber: '',
    };

    // Update form state directly
    setValue('stateLicenses', [...watchStateLicenses, newLicense]);
  };

  // Function to remove a state license
  const removeStateLicense = (index: number) => {
    const updatedLicenses = [...watchStateLicenses];
    updatedLicenses.splice(index, 1);

    // Update form state directly
    setValue('stateLicenses', updatedLicenses);
  };

  // Handle form submission
  const onSubmit = handleSubmit(
    (formData: OrganizationalUnit) => {
      console.log('Form submitted with data:', formData);

      // Create a sanitized version of the org unit without circular references
      const orgUnitToSave = {
        ...formData,
        // Add required properties
        tenantId: tenant?.tenantId || undefined,
        parentOrgUnitId: parentOrgUnitId || orgUnit?.parentOrgUnitId || undefined,
        // Preserve the orgUnitId when in edit mode (must be after formData spread to ensure it's not overwritten)
        orgUnitId: orgUnit?.orgUnitId || formData.orgUnitId, // Keep original ID when editing
        // Remove any nested children to prevent circular references
        children: undefined,
      } as unknown as OrganizationalUnit;

      console.log('Calling onSave with data:', orgUnitToSave);

      // Pass data to parent component for saving
      onSave(orgUnitToSave);
    },
    (errors) => {
      // This second callback is called when validation fails
      console.error('Form validation failed with errors:', errors);
      showFormErrors();
      return false;
    }
  );

  const stateOptions = usStates.map((state: USState) => ({
    label: state.name,
    value: state.abbreviation,
  }));

  const licenseStatusOptions = Object.values(LicenseStatus).map((status) => ({
    label: status,
    value: status,
  }));

  const channelOptions = Object.values(Channel).map((channel) => ({
    label: channel,
    value: channel,
  }));

  return (
    <div className="p-3">
      <FormContainer onSubmit={onSubmit} onCancel={onHide} loading={localApiLoading} errors={errors}>
        <div className="card">
          <Messages ref={errors} />

          <div className="formgrid grid">
            <div className="field col-12 md:col-6">
              <FormInputText
                control={control}
                errors={formErrors}
                field="name"
                label="Organization Name"
                placeholder="Enter organization name"
              />
            </div>

            <div className="field col-12 md:col-6">
              <FormDropdown
                control={control}
                errors={formErrors}
                field="channel"
                label="Channel"
                options={channelOptions}
                placeholder="Select channel"
                showClear={true}
              />
            </div>

            <div className="field col-12 md:col-6">
              <FormInputText
                control={control}
                errors={formErrors}
                field="nmlsId"
                label="NMLS ID"
                placeholder="Enter NMLS ID"
              />
            </div>

            <div className="field col-12">
              <FormCheckbox control={control} errors={formErrors} field="isOriginatingBranch" label="Originating Branch" />
            </div>

            <div className="field col-12">
              <div
                className="my-2 p-2 bg-gray-100 border-round mx-0"
                style={{ marginLeft: '-1rem', marginRight: '-1rem', borderRadius: 0, paddingLeft: '1rem' }}
              >
                <div className="flex align-items-center">
                  <i className="pi pi-map-marker mr-2"></i>
                  <span className="font-bold text-lg">Address</span>
                </div>
              </div>
            </div>

            <div className="field col-12">
              <FormGoogleAddressLookup
                control={control}
                errors={formErrors}
                field="address.street"
                label="Street Address"
                placeholder="Start typing an address..."
                onAddressSelected={(components: AddressComponents) => {
                  setValue('address.city', components.city || '');
                  setValue('address.state', components.state || '');
                  setValue('address.postalCode', components.zipCode || '');
                  setValue('address.street', components.address || '');
                }}
              />
            </div>

            <div className="field col-12 md:col-4">
              <FormInputText
                control={control}
                errors={formErrors}
                field="address.unitNumber"
                label="Apt./Unit/Suite"
                placeholder="Enter unit, apt, or suite"
              />
            </div>

            <div className="field col-12 md:col-4">
              <FormInputText
                control={control}
                errors={formErrors}
                field="address.city"
                label="City"
                placeholder="Enter city"
              />
            </div>

            <div className="field col-12 md:col-4">
              <FormDropdown
                control={control}
                errors={formErrors}
                field="address.state"
                label="State"
                options={stateOptions}
                placeholder="Select state"
              />
            </div>

            <div className="field col-12 md:col-4">
              <FormInputText
                control={control}
                errors={formErrors}
                field="address.postalCode"
                label="Zip Code"
                placeholder="Enter postal code"
              />
            </div>

            <div className="field col-12">
              <div
                className="my-2 p-2 bg-gray-100 border-round mx-0"
                style={{ marginLeft: '-1rem', marginRight: '-1rem', borderRadius: 0, paddingLeft: '1rem' }}
              >
                <div className="flex align-items-center">
                  <i className="pi pi-id-card mr-2"></i>
                  <span className="font-bold text-lg">State Licenses</span>
                </div>
              </div>
            </div>

            {watchStateLicenses.map((license, index) => (
              <div key={index} className="p-3 mb-3 border-1 border-round relative col-12">
                <Button
                  icon="pi pi-trash"
                  className="p-button-danger p-button-rounded p-button-text absolute"
                  style={{ top: '10px', right: '10px' }}
                  onClick={() => removeStateLicense(index)}
                  tooltip="Remove License"
                  tooltipOptions={{ position: 'top', showDelay: 100, hideDelay: 100 }}
                />

                <div className="flex justify-content-between align-items-center mb-3">
                  <span className="font-semibold text-lg">License #{index + 1}</span>
                </div>

                <div className="formgrid grid">
                  <div className="field col-12 md:col-4">
                    <FormDropdown
                      control={control}
                      errors={formErrors}
                      field={`stateLicenses.${index}.state` as any}
                      label="State"
                      options={stateOptions}
                      placeholder="Select State"
                    />
                  </div>

                  <div className="field col-12 md:col-4">
                    <FormDropdown
                      control={control}
                      errors={formErrors}
                      field={`stateLicenses.${index}.status` as any}
                      label="Status"
                      options={licenseStatusOptions}
                      placeholder="Select Status"
                    />
                  </div>

                  <div className="field col-12 md:col-4">
                    <FormInputText
                      control={control}
                      errors={formErrors}
                      field={`stateLicenses.${index}.licenseNumber` as any}
                      label="License Number"
                      placeholder="Enter license number"
                    />
                  </div>
                </div>
              </div>
            ))}

            <div className="col-12 flex justify-content-end mb-4">
              <Button
                label="Add State License"
                icon="pi pi-plus"
                className="p-button-outlined"
                onClick={addStateLicense}
                type="button"
              />
            </div>
          </div>
        </div>
      </FormContainer>
    </div>
  );
}
